import BaseModel from './BaseModel';
import Ref from './Ref';
import Tag from './Tag';
import { sanitize_pricing_tags } from '../services/pricingTags';
import Utils from '../services/Utils';
import * as _ from 'es-toolkit';

const normalize_optional_money_map = (map) => {
    const next = {};
    Object.keys(map || {}).forEach((currency) => {
        const raw = map[currency];
        if (raw === '' || raw === null || raw === undefined)
            return;
        const amount = Number(raw);
        if (Number.isFinite(amount))
            next[currency] = amount;
    });
    return next;
};

/** Class representing a pricing. */
class Pricing extends BaseModel {
    constructor(properties) {
        super(properties);

        if ('VAT' in this) {
            this.VAT = parseFloat(this.VAT);
        }

        if ('idiv' in this && this.idiv !== null && this.idiv !== undefined && this.idiv !== '') {
            this.idiv = parseFloat(this.idiv);
        }

        if ('refs' in this)
            this.refs = (this.refs || []).map(ref => new Ref(ref));

        this.tags = this.tags || [];
        this.tags = (this.tags || []).map(tag => new Tag(tag));

        this.rules = this.rules || {};

        if ('wallet_amount' in this)
            this.wallet_amount = Pricing.hydrate_optional_money_map(this.wallet_amount);
        if ('value_per_screening' in this)
            this.value_per_screening = Pricing.hydrate_optional_money_map(this.value_per_screening);
        if ('min_price' in this)
            this.min_price = Pricing.hydrate_optional_money_map(this.min_price);

        // One-way purge of accidental legacy keys (Engine owns migration).
        delete this.sellers;
        delete this.category;
        delete this.categories;
    }

    /** Bind optional money fields in forms: empty server maps stay blank, 0 stays 0. */
    static hydrate_optional_money_map(map) {
        const currency = Utils.currency();
        if (!map || typeof map !== 'object' || Array.isArray(map))
            return { [currency]: '' };
        if (Object.keys(map).length === 0)
            return { [currency]: '' };
        const hydrated = { ...map };
        if (!(currency in hydrated))
            hydrated[currency] = '';
        return hydrated;
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();

        _.mapKeys(prepared.price || {}, (price, currency) => {
            prepared.price[currency] = Number(price);
        });

        prepared.variable_price = !!prepared.variable_price;
        prepared.value_like_price = !!prepared.value_like_price;

        if (!prepared.variable_price) {
            delete prepared.min_price;
        } else if (prepared.min_price) {
            const next_min = {};
            Object.keys(prepared.min_price).forEach((cur) => {
                const raw = prepared.min_price[cur];
                if (raw === '' || raw === null || raw === undefined)
                    return;
                const amount = Number(raw);
                if (Number.isFinite(amount))
                    next_min[cur] = amount;
            });
            if (Object.keys(next_min).length === 0)
                delete prepared.min_price;
            else
                prepared.min_price = next_min;
        }

        // When value follows the paid price, drop a stale explicit value so Engine
        // clones price → value and clients use resolveCartAmounts(sale).
        if (prepared.value_like_price) {
            delete prepared.value;
        } else if (prepared.value && typeof prepared.value === 'object') {
            _.mapKeys(prepared.value, (value, currency) => {
                prepared.value[currency] = Number(value);
            });
        }

        if ('value_per_screening' in prepared) {
            prepared.value_per_screening = normalize_optional_money_map(prepared.value_per_screening);
        }

        if ('wallet_amount' in prepared) {
            const normalized_wallet = normalize_optional_money_map(prepared.wallet_amount);
            if (Object.keys(normalized_wallet).length === 0)
                delete prepared.wallet_amount;
            else
                prepared.wallet_amount = normalized_wallet;
        }

        if (!prepared.description || Object.values(prepared.description).length === 0)
            delete prepared.description;

        prepared.opaque.eshop_sort_weight = parseInt(prepared.opaque?.eshop_sort_weight || 0, 10);

        prepared.VAT = Math.min(Math.max(0, parseFloat(this.VAT)), 100);

        if ('idiv' in this && this.idiv !== null && this.idiv !== undefined && this.idiv !== '') {
            prepared.idiv = Math.min(Math.max(0, parseFloat(this.idiv)), 100);
        } else {
            delete prepared.idiv;
        }

        if (_.isArray(prepared.posters)) {
            prepared.posters = prepared.posters.filter(p => _.isObject(p) && _.isString(p.url) && p.url.trim() !== "");
        } else {
            prepared.posters = [];
        }

        prepared.tags = sanitize_pricing_tags(prepared.tags);

        prepared.rules = prepared.rules || {};

        // One-way purge of accidental legacy top-level keys (Engine owns migration).
        delete prepared.sellers;
        delete prepared.category;
        delete prepared.categories;

        // only_for_roles is optional: missing = inherit TT / unrestricted.
        // Keep [] (nobody). Strip Kronos catch-all shim (full pricingRoles list).
        if (!Array.isArray(prepared.rules.only_for_roles)) {
            delete prepared.rules.only_for_roles;
        } else {
            prepared.rules.only_for_roles = prepared.rules.only_for_roles.filter(
                role => typeof role === 'string' && role !== ''
            );
            const catch_all = Utils.pricingRoles();
            if (
                prepared.rules.only_for_roles.length === catch_all.length
                && [...prepared.rules.only_for_roles].sort().join('\0')
                    === [...catch_all].sort().join('\0')
            ) {
                delete prepared.rules.only_for_roles;
            }
        }

        // Keep [] (nobody / no targets). Missing key = unrestricted / inherit.
        if (!Array.isArray(prepared.rules.only_for_salepoints))
            delete prepared.rules.only_for_salepoints;

        if (!Array.isArray(prepared.rules.only_for_tickettypes))
            delete prepared.rules.only_for_tickettypes;

        if (!Array.isArray(prepared.rules.exclude_tickettypes))
            delete prepared.rules.exclude_tickettypes;

        if (!Array.isArray(prepared.rules.hides_pricings_while_available)) {
            delete prepared.rules.hides_pricings_while_available;
        } else {
            prepared.rules.hides_pricings_while_available = prepared.rules.hides_pricings_while_available.filter(
                id => typeof id === 'string' && id !== ''
            );
        }

        if (!Array.isArray(prepared.rules.only_for_categories)) {
            delete prepared.rules.only_for_categories;
        } else {
            prepared.rules.only_for_categories = prepared.rules.only_for_categories.filter(
                c => typeof c === 'string' && c !== ''
            );
        }

        const not_before = prepared.rules.not_before;
        if (!not_before || _.isNull(not_before) || not_before === "")
            delete prepared.rules.not_before;

        const not_after = prepared.rules.not_after;
        if (!not_after || _.isNull(not_after) || not_after === "")
            delete prepared.rules.not_after;

        delete prepared.rules.max_quantity;

        if (Object.keys(prepared.rules).length === 0)
            prepared.rules = {};

        return prepared;
    }
}

export default Pricing;
